import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Download, Search, SlidersHorizontal, FilterX } from "lucide-react";
import { FilterType } from "../types/userTypes";
import { toast } from "sonner";

interface UserListActionsProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  activeFilter: FilterType | null;
  onFilterChange: (filter: FilterType | null) => void;
  onExport: (format: 'csv' | 'excel' | 'pdf') => void;
}

const UserListActions: React.FC<UserListActionsProps> = ({
  searchTerm,
  onSearchChange,
  activeFilter,
  onFilterChange,
  onExport,
}) => {
  const handleFilter = (filter: FilterType) => {
    onFilterChange(filter);
    toast.success(`Filter applied: ${filter}`);
  };

  const handleClearFilter = () => {
    onFilterChange(null);
    toast.success("Filters cleared");
  };

  const getFilterName = (filter: FilterType): string => {
    switch (filter) {
      case 'admin':
        return 'Admin Users';
      case 'user':
        return 'Regular Users';
      default:
        return filter;
    }
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          className="pl-8"
          value={searchTerm}
          onChange={onSearchChange}
        />
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Filters
                {activeFilter && <span className="ml-1 h-2 w-2 rounded-full bg-bike-primary"></span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleFilter('admin')}>
                Admin Users
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilter('user')}>
                Regular Users
              </DropdownMenuItem>
              {activeFilter && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleClearFilter} className="text-destructive focus:bg-destructive/10">
                    <FilterX className="mr-2 h-4 w-4" />
                    Clear Filters
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {activeFilter && (
            <Button 
              variant="secondary" 
              size="sm" 
              className="bg-bike-primary text-white hover:bg-bike-primary/90 flex items-center gap-2"
              onClick={handleClearFilter}
            >
              {getFilterName(activeFilter)}
              <FilterX className="h-4 w-4" />
            </Button>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport('csv')}>
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('excel')}>
              Export as Excel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('pdf')}>
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default UserListActions;
